import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { AuthShell } from '@/components/AuthShell';
import { CodeInput } from '@/components/CodeInput';
import { PasswordPolicy, passwordMeetsPolicy } from '@/components/PasswordPolicy';

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError(t('forceChange.mismatch'));
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch('/internal/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          newPassword,
          ...(needsTotp && totpCode.length === 6 ? { totpCode } : {}),
        }),
      });

      if (res.ok) {
        setDone(true);
        return;
      }

      const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      if (data.error === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
      } else {
        setError(data.message || t('reset.invalid'));
      }
    } catch {
      setError(t('common:error'));
    } finally {
      setIsLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell>
        <div className="px-4 py-3 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
          {t('reset.invalid')}
        </div>
        <button
          type="button"
          onClick={() => void navigate('/login')}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm bg-white/10 border border-white/[0.15] hover:bg-white/15 transition-colors"
        >
          {t('reset.goLogin')}
        </button>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div className="flex items-start gap-3 px-4 py-3.5 rounded-lg bg-emerald-500/15 border border-emerald-400/25">
          <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-100">{t('reset.success')}</p>
        </div>
        <button
          type="button"
          onClick={() => void navigate('/login')}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300"
        >
          {t('reset.goLogin')}
        </button>
      </AuthShell>
    );
  }

  const canSubmit =
    passwordMeetsPolicy(newPassword) &&
    confirm.length > 0 &&
    (!needsTotp || totpCode.length === 6) &&
    !isLoading;

  return (
    <AuthShell>
      <div>
        <h2 className="text-xl font-bold text-white">{t('reset.title')}</h2>
        <p className="text-white/60 mt-1 text-sm">{t('reset.subtitle')}</p>
      </div>

      {error && (
        <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
          {error}
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-white/80 mb-1.5">
            {t('forceChange.newPassword')}
          </label>
          <input
            type="password"
            id="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={isLoading}
            autoComplete="new-password"
            className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
          />
        </div>

        <PasswordPolicy password={newPassword} />

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-white/80 mb-1.5">
            {t('forceChange.confirmPassword')}
          </label>
          <input
            type="password"
            id="confirm-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={isLoading}
            autoComplete="new-password"
            className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
          />
        </div>

        {needsTotp && (
          <div className="space-y-2">
            <p className="text-xs text-white/60">{t('reset.totpNote')}</p>
            <CodeInput value={totpCode} onChange={setTotpCode} disabled={isLoading} />
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {isLoading ? t('reset.submitting') : t('reset.submit')}
        </button>
      </form>
    </AuthShell>
  );
}
